import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { API_BASE_URL } from '../config';

export default function UserPreferences() {
  const [cookies] = useCookies(['access_token']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preferences, setPreferences] = useState([]);
  const [searchState, setSearchState] = useState({}); // { [ingredientKey]: { query, results, open } }

  const userId = typeof window !== 'undefined' ? window.localStorage.getItem('userID') : null;

  const authHeaders = cookies.access_token
    ? { Authorization: `Bearer ${cookies.access_token}` }
    : {};

  const fetchPreferences = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/recipes/preferences/${userId}`, {
        headers: authHeaders
      });
      const prefs = (res.data && Array.isArray(res.data.preferences)) ? res.data.preferences : [];
      setPreferences(prefs);
    } catch (e) {
      setError('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, [cookies.access_token]);

  const openSearch = (ingredientName) => {
    setSearchState(prev => ({
      ...prev,
      [ingredientName]: { query: ingredientName, results: [], open: true }
    }));
  };

  const closeSearch = (ingredientName) => {
    setSearchState(prev => ({
      ...prev,
      [ingredientName]: { ...(prev[ingredientName] || {}), open: false }
    }));
  };

  const updateQuery = (ingredientName, query) => {
    setSearchState(prev => ({
      ...prev,
      [ingredientName]: { ...(prev[ingredientName] || {}), query }
    }));
  };

  const runSearch = async (ingredientName) => {
    try {
      const q = (searchState[ingredientName]?.query || ingredientName).trim();
      const res = await axios.get(`${API_BASE_URL}/ingredients/search`, {
        params: { q, limit: 50 },
        headers: authHeaders
      });
      const results = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setSearchState(prev => ({
        ...prev,
        [ingredientName]: { ...(prev[ingredientName] || {}), results }
      }));
    } catch (e) {
      // ignore
    }
  };

  const savePreference = async (recipeIngredient, dbIng) => {
    if (!userId || !cookies.access_token) return;
    try {
      const preference = {
        recipeIngredient,
        dbIngredient: {
          ingredientId: dbIng._id || dbIng.id,
          name: dbIng.name,
          brand: dbIng.brand || ''
        }
      };
      await axios.post(
        `${API_BASE_URL}/recipes/save-single-preference`,
        { userId, preference },
        { headers: { 'Content-Type': 'application/json', ...authHeaders } }
      );
      // Refresh list
      await fetchPreferences();
      closeSearch(recipeIngredient);
    } catch (e) {
      // ignore
    }
  };

  const deletePreference = async (recipeIngredient) => {
    if (!userId || !cookies.access_token) return;
    try {
      await axios.delete(`${API_BASE_URL}/recipes/preferences/${userId}/${encodeURIComponent(recipeIngredient)}`, {
        headers: authHeaders
      });
      setPreferences(prev => prev.filter(p => (p?.recipeIngredient || '') !== recipeIngredient));
    } catch (e) {
      // ignore
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h2 className="text-2xl font-semibold mb-4">My Ingredient Preferences</h2>
      {loading && <div>Loading...</div>}
      {error && <div className="text-red-600 mb-2">{error}</div>}
      {!loading && preferences.length === 0 && (
        <div className="text-gray-600">No preferences saved yet. Choose brands in recipe cost pages and they will auto-save here.</div>
      )}
      <div className="space-y-4">
        {preferences.map((pref, idx) => {
          const ingredientName = pref?.recipeIngredient || '';
          const current = pref?.dbIngredient || {};
          const search = searchState[ingredientName] || { query: ingredientName, results: [], open: false };
          return (
            <div key={idx} className="bg-white rounded-md shadow p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm text-gray-500">Ingredient</div>
                  <div className="text-lg font-medium">{ingredientName}</div>
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-500">Preferred Brand</div>
                  <div className="text-base">{current?.name}{current?.brand ? ` — ${current.brand}` : ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  {!search.open ? (
                    <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={() => openSearch(ingredientName)}>Change</button>
                  ) : (
                    <button className="px-3 py-2 bg-gray-200 rounded" onClick={() => closeSearch(ingredientName)}>Close</button>
                  )}
                  <button className="px-3 py-2 bg-red-600 text-white rounded" onClick={() => deletePreference(ingredientName)}>Delete</button>
                </div>
              </div>
              {search.open && (
                <div className="mt-3 border-t pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      className="border rounded px-3 py-2 w-full"
                      placeholder={`Search brands for ${ingredientName}`}
                      value={search.query}
                      onChange={(e) => updateQuery(ingredientName, e.target.value)}
                    />
                    <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={() => runSearch(ingredientName)}>Search</button>
                  </div>
                  <div className="max-h-60 overflow-auto border rounded">
                    {Array.isArray(search.results) && search.results.length > 0 ? (
                      search.results.map((r) => (
                        <button
                          key={r._id || r.id}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex justify-between"
                          onClick={() => savePreference(ingredientName, r)}
                        >
                          <span>{r.name}{r.brand ? ` — ${r.brand}` : ''}</span>
                          <span className="text-sm text-gray-500">{r.packageInfo || r.details || ''}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500">No results</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


